import { useMutation, useQuery } from '@tanstack/react-query';
import { List, ListItem, Toggle } from 'framework7-react';
import { FC, Fragment, ReactElement, useEffect, useMemo, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import dayjs from 'dayjs';
import { groupBy, orderBy, uniq, uniqBy } from 'lodash';
import pluralize from 'pluralize';

import API from '@api/API';

import { useAppSettingsQuery } from '@queries/AppQueries';
import { Badge, useBadgeById } from '@queries/CritiqueQueries';
import { useCurrentEntertainerQuery, useEntertainerDirectory } from '@queries/EntertainerQueries';
import { useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { useFeaturedLooksFeedPostsQuery } from '@queries/PostQueries';
import {
  Series,
  useSeriesByTagId,
  useSeriesByTagSlug,
  useSeriesEntertainersQuery,
} from '@queries/TagQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useSendEntertainerBadge } from '@mutations/EntertainerMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { CurrentCity, useCitiesQuery, useCityById } from '@hooks/useCurrentUserLocation';

import {
  EntertainerDashboardAction,
  useLocation,
  useNavigate,
  useParams,
  useRouter,
} from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import EventCard, {
  EventActions,
  EventHeader,
  EventImage,
  PromoterHeader,
} from '@components/cards/EventCard';
import { VenueCard } from '@components/cards/VenueCard';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import { ButtonFilter } from '@components/layout/ButtonFilter/ButtonFilter';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { ErrorBoundary } from '@components/layout/ErrorBoundary';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LinkBanner } from '@components/layout/LinkBanner/LinkBanner';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { SeriesEntertainersList } from '@screens/series/SeriesScreen/SeriesScreen';
import {
  SweepstakesCard,
  useSweepstakesReferralLink,
} from '@screens/series/SeriesScreen/SweepstakesCard';

import { CitySelector } from '../DragKingCommunityScreen/DragKingThingShoutoutsScreen';
import { KingOfDragBanner } from '../DragKingCommunityScreen/KingOfDragBanner';
import { ScheduledSocialPost } from '../DragKingCommunityScreen/ScheduledSocialPost';
import { useCityDragKingSeriesEntertainers } from '../DragKingCommunityScreen/useCityDragKingSeriesEntertainers';
import { useCityDragKingSeriesEvents } from '../DragKingCommunityScreen/useCityDragKingSeriesEvents';
import { useCityDragKingSeriesMostBookedEntertainers } from '../DragKingCommunityScreen/useCityDragKingSeriesMostBookedEntertainers';
import { FeaturedKing } from '../EntertainerDetailsScreen/assets/FeaturedKing';
import { LandingPageSweepstakesCard } from '../EntertainerSweepstakesScreen/EntertainerSweepstakesScreen';
import {
  FeaturedLook,
  FeaturedLookPopup,
  useFeaturedLooksQuery,
} from '../FeaturedLooksScreen/FeaturedLooksScreen';
import {
  AddFeaturedPhotoPopup,
  AddImageContent,
  FeaturedPhotoManagementSection,
} from '../components/FeaturedPhotoManagementSection';
import {
  DirectoryTabContent,
  FavoritesTabContent,
  JoinTabContent,
  SuggestedTabContent,
  TopKingMaterialPopup,
} from './TopKingMaterialScreen';
import tickets from './tickets.svg';

interface DragKingCommunityScreenProps {}

interface DragKingCommunitySubmission {
  _id: string;
  title: string;
  image?: string;
  status: string;
  entertainer?: {
    _id: string;
    title: string;
    bookingHandle: string;
  };
}

const useDragKingCommunitySubmissions = (isEnabled: boolean) => {
  const { data, isFetched } = useQuery({
    queryKey: ['dragKingCommunitySubmissions'],
    queryFn: () =>
      API.get<{ submissions: DragKingCommunitySubmission[] }>(
        `/v1/drag-king-community/submissions`,
      ),
    enabled: isEnabled,
  });

  return {
    submissions: data?.submissions,
    areSubmissionsReady: isFetched,
  };
};

const useUpdateDragKingCommunitySubmission = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      entertainerMediaId,
      status,
      temp,
    }: {
      entertainerMediaId: string;
      status: 'approved' | 'rejected';
      temp?: {
        entertainerInstagram?: string;
        base64Image?: any;
        path?: string;
        title?: string;
        addCollaborator?: boolean;
      };
    }) =>
      API.post(`/v1/drag-king-community/submissions`, {
        entertainerMediaId,
        status,
        temp,
      }),
  });

  return {
    isUpdatingSubmission: isPending,
    updateSubmissionAsync: mutateAsync,
  };
};

const DragKingSubmissionCard: FC<{
  submission: DragKingCommunitySubmission;
}> = ({ submission }) => {
  const [status, setStatus] = useState(submission.status);
  const [title, setTitle] = useState(submission.entertainer?.title);
  const { updateSubmissionAsync } = useUpdateDragKingCommunitySubmission();

  const handleApprove = async () => {
    setStatus('approved');
    await updateSubmissionAsync({
      entertainerMediaId: submission._id,
      status: 'approved',
      temp: {
        path: submission.image, // for the sake of the API, we need to send the image path
        title, // this is required for the API to update the title of the media
      },
    });
  };

  const handleReject = async () => {
    setStatus('rejected');
    await updateSubmissionAsync({
      entertainerMediaId: submission._id,
      status: 'rejected',
      temp: {
        path: submission.image, // for the sake of the API, we need to send the image path
        title, // this is required for the API to update the title of the media
      },
    });
  };

  return (
    <div className="d-grid gap-2">
      <img
        src={submission.image}
        alt={submission.title}
        className="w-100"
        style={{ borderRadius: 8 }}
      />
      <Input
        defaultValue={submission.entertainer.title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <div className="d-flex gap-2">
        <Button
          roundness="rounded"
          color="neutral"
          variant={status === 'approved' ? 'default' : 'outlined'}
          onClick={handleApprove}
        >
          Approve
        </Button>
        <Button
          roundness="rounded"
          color="neutral"
          variant={status === 'rejected' ? 'default' : 'outlined'}
          onClick={handleReject}
        >
          Reject
        </Button>
      </div>
    </div>
  );
};

const AddCustomDragKingSubmissionPopupContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [entertainerInstagram, setInstagram] = useState('');
  const [base64Image, setUpdatedImage] = useState<string | ArrayBuffer>();
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('approved');
  const [addCollaborator, setAddCollaborator] = useState(true);

  const { isUpdatingSubmission, updateSubmissionAsync } = useUpdateDragKingCommunitySubmission();

  const handleSubmit = async () => {
    await updateSubmissionAsync({
      entertainerMediaId: '',
      status: status as any,
      temp: {
        entertainerInstagram,
        base64Image,
        title,
        addCollaborator,
      },
    });

    onClose();
  };

  return (
    <div className="d-grid gap-2">
      <Select
        options={[
          { value: 'approved', label: 'Approved' },
          { value: 'pending', label: 'Pending' },
        ]}
        value={status}
        onChange={(e) => setStatus(e.target.value)}
      />
      <Input
        placeholder="Stage Name *"
        name="stageName"
        onChange={(event) => setTitle(event.target.value)}
      />
      <Input
        placeholder="Instagram *"
        name="instagram"
        onChange={(event) => setInstagram(event.target.value)}
      />
      <div className="d-flex gap-2 align-items-center">
        <Toggle defaultChecked={addCollaborator} onToggleChange={setAddCollaborator} />
        <div>
          <strong>Add Collaborator</strong>
        </div>
      </div>
      <Card>
        <ImageUploadField
          name="customSubmissionImage"
          placeholder="Select an image"
          onChange={(blob) => setUpdatedImage(blob)}
        />
      </Card>
      <div>
        <Button
          roundness="rounded"
          disabled={!base64Image || !title || isUpdatingSubmission}
          onClick={handleSubmit}
        >
          {isUpdatingSubmission ? 'Uploading...' : 'Upload'}
        </Button>
      </div>
    </div>
  );
};

const AddCustomDragKingSubmissionPopup: FC<{ isVisible: boolean; onClose: () => void }> = ({
  isVisible,
  onClose,
}) => {
  return (
    <Popup title="Add Custom" isVisible={isVisible} onClose={onClose} height="full">
      <Popup.Content>
        {isVisible && <AddCustomDragKingSubmissionPopupContent onClose={onClose} />}
      </Popup.Content>
    </Popup>
  );
};

const useShareEntertainerMediaPostMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ entertainerMediaId, title }: { entertainerMediaId: string; title?: string }) =>
      API.post(`/v1/entertainer-media/${entertainerMediaId}/share-badges-post`, {
        title,
      }),
  });

  return {
    isSharing: isPending,
    shareEntertainerMediaPostAsync: mutateAsync,
  };
};

const FeaturedLookGrid: FC<{
  isLoading: boolean;
  featuredLooks: FeaturedLook[];
  onSelectLook: (lookId: string) => void;
}> = ({ isLoading, featuredLooks, onSelectLook }) => {
  return (
    <>
      {!isLoading ? (
        <div className="d-grid grid-cols-3 gap-1 px-1">
          {featuredLooks.map(({ _id, image }, i) => (
            <div key={_id} style={{ aspectRatio: 1 }} onClick={() => onSelectLook(_id)}>
              <AsyncImage
                src={image}
                style={{
                  display: 'block',
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover',
                }}
              />
            </div>
          ))}
        </div>
      ) : (
        <Skeleton height={400} />
      )}
    </>
  );
};

const AwardPersonalizeBadgePopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  entertainerMedia: {
    _id: string;
    title: string;
    image: string;
  };
  entertainer: {
    _id: string;
    title: string;
  };
  badge?: Badge;
  header?: ReactElement;
}> = ({ entertainerMedia, entertainer, isVisible, onClose, badge, header }) => {
  const { track } = useAnalytics();
  const { createAuthGateHandler } = useAuthGate();
  const { sendEntertainerBadgeAsync, isSubmitting } = useSendEntertainerBadge();
  const [didSend, setDidSend] = useState(false);

  if (!badge) {
    return <></>;
  }

  const headerImageSize = 64;
  const handleNominate = () => {
    track('Send Personalized Badge', {
      campaign: badge.title,
      entertainerId: entertainer._id,
      entertainerMediaId: entertainerMedia._id,
    });

    createAuthGateHandler(
      async () => {
        await sendEntertainerBadgeAsync({
          entertainerId: entertainer._id,
          entertainerMediaId: entertainerMedia._id,
          badgeId: badge._id,
          reason: 'Look',
          meta: {},
        });
        setDidSend(true);
      },
      {
        entertainerCallback: () => {},
      },
    );
  };

  return (
    <Popup isVisible={isVisible} onClose={onClose} title={badge.title} height="full">
      <Popup.Content>
        {entertainerMedia && (
          <>
            <TitleToolbar
              className="mb-2"
              size="md"
              text={entertainer.title}
              action={
                <Button
                  className="py-2 px-3"
                  disabled={isSubmitting || didSend}
                  roundness="rounded"
                  size="sm"
                  onClick={handleNominate}
                >
                  {didSend ? 'Awarded' : 'Award my badge'}
                </Button>
              }
            />
            <div style={{ position: 'relative' }}>
              <AsyncImage src={entertainerMedia.image} style={{ width: '100%' }} />
            </div>

            <div style={{}}>{entertainer.title}</div>
            <div className="mb-3" style={{ fontSize: 24, lineHeight: 1.2 }}>
              <strong>{entertainerMedia.title}</strong>
            </div>
            <div style={{ borderRadius: 8, background: 'rgba(255,255,255,.1)' }}>
              <div className="p-2">
                <div className="d-flex gap-3 align-items-center">
                  <div>
                    <AsyncImage
                      src={badge.imageUrl}
                      placeholder={
                        <div style={{ height: headerImageSize, width: headerImageSize }} />
                      }
                      style={{
                        display: 'block',
                        height: headerImageSize,
                        width: headerImageSize,
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 16, lineHeight: 1.2 }}>
                      <strong>{badge.title}</strong>
                    </div>
                    <div>Use your platform to give recognition to this artist with your badge!</div>
                  </div>
                </div>
                <div className="mt-2">
                  <Button
                    disabled={isSubmitting || didSend}
                    roundness="rounded"
                    onClick={handleNominate}
                  >
                    {didSend ? 'Awarded' : `Award ${badge.title}`}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </Popup.Content>
    </Popup>
  );
};

const VIEW_AS_USER = false;

interface PersonalizedBadgeScreenProps {
  badgeId: string;
}

const DEFAULT_TAB_OPTIONS = [
  { value: 'favorites', label: 'Favorites' },
  { value: 'join', label: 'Upload' },
];

const OWNER_TAB_OPTIONS = [
  { value: 'favorites', label: 'Favorites' },
  { value: 'pick', label: 'Make Picks' },
  { value: 'directory', label: 'Directory' },
];

export const PersonalizedBadgeScreen = createScreenComponent<PersonalizedBadgeScreenProps>(
  ({ f7route, badgeId }) => {
    const { track } = useAnalytics();
    const [tabValue, setTabValue] = useState('suggested');

    const { featuredLooksAreReady, featuredLooks = [] } = useFeaturedLooksQuery({});
    const { badge } = useBadgeById(badgeId);

    const [selectedLookId, setSelectedLookId] = useState<string>();
    const currentFeaturedLook = useMemo<FeaturedLook | undefined>(() => {
      return featuredLooks.find(({ _id }) => _id === selectedLookId);
    }, [selectedLookId, featuredLooks]);

    const handleSelectLook = (lookId: string) => {
      track('Look Selected', {
        campaign: badge?.title,
        lookId,
      });
      setSelectedLookId(lookId);
    };

    const headerImageSize = 64;

    return (
      <Screen
        name="Personalized Badge Screen"
        hideToolbar
        headerProps={{
          title: <Screen.GagglTitle />,
          right: <ShareLinkButton url={`https://gaggl.app/top-king-material`} />,
        }}
      >
        <Screen.Content>
          <div className="mb-3">
            <KingOfDragBanner context="Personalized Badge Screen" />
          </div>
          {badge && (
            <>
              <div className="d-flex mb-3 gap-3 align-items-center">
                <div>
                  <AsyncImage
                    src={badge.imageUrl}
                    placeholder={
                      <div style={{ height: headerImageSize, width: headerImageSize }} />
                    }
                    style={{
                      display: 'block',
                      height: headerImageSize,
                      width: headerImageSize,
                      objectFit: 'cover',
                    }}
                  />
                </div>
                <div>
                  <strong className="fs-5">{badge.title}</strong>
                  <br />
                  <span>{badge.description}. Submit your own!</span>
                </div>
              </div>
              <ButtonFilter
                selected={tabValue}
                options={badge.userCanSendBadge ? OWNER_TAB_OPTIONS : DEFAULT_TAB_OPTIONS}
                onChange={(value) => {
                  track('Tab Changed', { tab: value });
                  setTabValue(value);
                }}
              />
              {tabValue === 'favorites' && (
                <FavoritesTabContent
                  isInfiniteScrollEnabled
                  badgeId={badgeId}
                  onSelectLook={handleSelectLook}
                  emptyState={{
                    title: 'No favorites yet',
                    text: `The ${badge?.title} has not been awarded to any looks yet.`,
                  }}
                />
              )}
              {tabValue === 'pick' && (
                <SuggestedTabContent isInfiniteScrollEnabled onSelectLook={handleSelectLook} />
              )}
              {tabValue === 'directory' && <DirectoryTabContent onSelectLook={handleSelectLook} />}
              {tabValue === 'join' && <JoinTabContent />}
            </>
          )}
        </Screen.Content>
        {badge &&
          (badge.userCanSendBadge ? (
            <AwardPersonalizeBadgePopup
              isVisible={!!selectedLookId}
              onClose={() => setSelectedLookId(undefined)}
              entertainerMedia={currentFeaturedLook}
              entertainer={currentFeaturedLook?.entertainer}
              badge={badge}
            />
          ) : (
            <TopKingMaterialPopup
              isVisible={!!selectedLookId}
              onClose={() => setSelectedLookId(undefined)}
              entertainerMedia={currentFeaturedLook}
              entertainer={currentFeaturedLook?.entertainer}
            />
          ))}
      </Screen>
    );
  },
);
